import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { 
  Ticket, Plus, Trash2, Edit2, Calendar, Percent, DollarSign, GripVertical, ArrowRight
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PlatformLayout } from "./platform-layout";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface CouponRule {
  id?: string;
  durationMonths: number | null;
  discountType: "percentage" | "fixed";
  discountValue: number;
}

interface Coupon {
  id: string;
  code: string;
  description: string | null;
  discountType: string;
  discountValue: number;
  appliesTo: string | null;
  appliesToTiers: string[] | null;
  maxUses: number | null;
  bonusLeadCredits: number | null;
  usedCount: number;
  validFrom: string | null;
  validUntil: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  rules: CouponRule[];
}

interface PermissionTier {
  id: string;
  name: string;
  displayName: string;
}

interface CouponFormData {
  code: string;
  description: string;
  discountType: "percentage" | "fixed";
  discountValue: string;
  selectedTiers: string[];
  maxUses: string;
  bonusLeadCredits: string;
  validFrom: string;
  validUntil: string;
  isActive: boolean;
  rules: CouponRule[];
}

const defaultFormData: CouponFormData = {
  code: "",
  description: "",
  discountType: "percentage",
  discountValue: "0",
  selectedTiers: [],
  maxUses: "",
  bonusLeadCredits: "",
  validFrom: "",
  validUntil: "",
  isActive: true,
  rules: [{ durationMonths: null, discountType: "percentage", discountValue: 0 }],
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString();
}

function formatDiscount(type: string, value: number): string {
  if (type === "percentage") return `${value}%`;
  return `$${(value / 100).toFixed(2)}`;
}

function formatRulesSummary(rules: CouponRule[]): string {
  if (!rules || rules.length === 0) return "No rules";
  
  return rules.map((rule, index) => {
    const discount = formatDiscount(rule.discountType, rule.discountValue);
    const duration = rule.durationMonths 
      ? `${rule.durationMonths} month${rule.durationMonths !== 1 ? 's' : ''}` 
      : 'forever';
    return `${discount} for ${duration}`;
  }).join(' → ');
}

export function CouponsContent() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [formData, setFormData] = useState<CouponFormData>(defaultFormData);

  const { data: coupons, isLoading } = useQuery<Coupon[]>({
    queryKey: ["/api/platform/coupons"],
  });

  const { data: tiers } = useQuery<PermissionTier[]>({
    queryKey: ["/api/platform/tiers"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: CouponFormData) => {
      return apiRequest("POST", "/api/platform/coupons", {
        code: data.code,
        description: data.description || null,
        discountType: data.rules[0]?.discountType || "percentage",
        discountValue: data.rules[0]?.discountValue || 0,
        appliesToTiers: data.selectedTiers.length > 0 ? data.selectedTiers : null,
        maxUses: data.maxUses ? parseInt(data.maxUses) : null,
        bonusLeadCredits: data.bonusLeadCredits ? parseInt(data.bonusLeadCredits) : 0,
        validFrom: data.validFrom || null,
        validUntil: data.validUntil || null,
        isActive: data.isActive,
        rules: data.rules.map(rule => ({
          durationMonths: rule.durationMonths,
          discountType: rule.discountType,
          discountValue: rule.discountValue,
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/coupons"] });
      toast({ title: "Coupon created successfully" });
      setDialogOpen(false);
      setFormData(defaultFormData);
    },
    onError: (error: any) => {
      toast({ 
        title: "Failed to create coupon", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CouponFormData }) => {
      return apiRequest("PATCH", `/api/platform/coupons/${id}`, {
        code: data.code,
        description: data.description || null,
        discountType: data.rules[0]?.discountType || "percentage",
        discountValue: data.rules[0]?.discountValue || 0,
        appliesToTiers: data.selectedTiers.length > 0 ? data.selectedTiers : null,
        maxUses: data.maxUses ? parseInt(data.maxUses) : null,
        bonusLeadCredits: data.bonusLeadCredits ? parseInt(data.bonusLeadCredits) : 0,
        validFrom: data.validFrom || null,
        validUntil: data.validUntil || null,
        isActive: data.isActive,
        rules: data.rules.map(rule => ({
          durationMonths: rule.durationMonths,
          discountType: rule.discountType,
          discountValue: rule.discountValue,
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/coupons"] });
      toast({ title: "Coupon updated successfully" });
      setDialogOpen(false);
      setSelectedCoupon(null);
      setFormData(defaultFormData);
    },
    onError: (error: any) => {
      toast({ 
        title: "Failed to update coupon", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/platform/coupons/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/coupons"] });
      toast({ title: "Coupon deleted successfully" });
      setDeleteDialogOpen(false);
      setSelectedCoupon(null);
    },
    onError: (error: any) => {
      toast({ 
        title: "Failed to delete coupon", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });

  const toggleActive = async (coupon: Coupon) => {
    updateMutation.mutate({ 
      id: coupon.id, 
      data: {
        ...formDataFromCoupon(coupon),
        isActive: !coupon.isActive,
      }
    });
  };

  const formDataFromCoupon = (coupon: Coupon): CouponFormData => ({
    code: coupon.code,
    description: coupon.description || "",
    discountType: coupon.discountType as "percentage" | "fixed",
    discountValue: coupon.discountType === "percentage" 
      ? coupon.discountValue.toString()
      : (coupon.discountValue / 100).toString(),
    selectedTiers: coupon.appliesToTiers || [],
    maxUses: coupon.maxUses?.toString() || "",
    bonusLeadCredits: coupon.bonusLeadCredits?.toString() || "",
    validFrom: coupon.validFrom ? coupon.validFrom.split("T")[0] : "",
    validUntil: coupon.validUntil ? coupon.validUntil.split("T")[0] : "",
    isActive: coupon.isActive,
    rules: coupon.rules && coupon.rules.length > 0 
      ? coupon.rules.map(r => ({
          durationMonths: r.durationMonths,
          discountType: r.discountType as "percentage" | "fixed",
          discountValue: r.discountValue,
        }))
      : [{ durationMonths: null, discountType: coupon.discountType as "percentage" | "fixed", discountValue: coupon.discountValue }],
  });

  const openCreateDialog = () => {
    setSelectedCoupon(null);
    setFormData(defaultFormData);
    setDialogOpen(true);
  };

  const openEditDialog = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setFormData(formDataFromCoupon(coupon));
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.code) {
      toast({ 
        title: "Coupon code is required", 
        variant: "destructive" 
      });
      return;
    }

    if (formData.rules.length === 0) {
      toast({ 
        title: "At least one discount rule is required", 
        variant: "destructive" 
      });
      return;
    }

    if (selectedCoupon) {
      updateMutation.mutate({ id: selectedCoupon.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const openDeleteDialog = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setDeleteDialogOpen(true);
  };

  const addRule = () => {
    setFormData(prev => ({
      ...prev,
      rules: [...prev.rules, { durationMonths: null, discountType: "percentage", discountValue: 0 }],
    }));
  };

  const removeRule = (index: number) => {
    setFormData(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index),
    }));
  };

  const updateRule = (index: number, updates: Partial<CouponRule>) => {
    setFormData(prev => ({
      ...prev,
      rules: prev.rules.map((rule, i) => i === index ? { ...rule, ...updates } : rule),
    }));
  };

  const toggleTier = (tierId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedTiers: prev.selectedTiers.includes(tierId)
        ? prev.selectedTiers.filter(id => id !== tierId)
        : [...prev.selectedTiers, tierId],
    }));
  };

  const getTierDisplayNames = (tierIds: string[] | null): string => {
    if (!tierIds || tierIds.length === 0) return "All tiers";
    if (!tiers) return tierIds.join(", ");
    return tierIds
      .map(id => {
        const tier = tiers.find(t => t.id === id);
        return tier?.displayName || tier?.name || id;
      })
      .join(", ");
  };

  return (
    <>
      <div className="p-3 sm:p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Coupon Codes</h1>
            <p className="text-muted-foreground">
              Create and manage discount codes for subscription tiers
            </p>
          </div>
          <Button onClick={openCreateDialog} data-testid="button-create-coupon">
            <Plus className="h-4 w-4 mr-2" />
            Create Coupon
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
          </div>
        ) : coupons?.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Ticket className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Coupons Yet</h3>
              <p className="text-muted-foreground text-center mb-4">
                Create your first coupon code to offer discounts on subscriptions
              </p>
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Create Coupon
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="h-5 w-5 text-red-600" />
                Active Coupons
              </CardTitle>
              <CardDescription>
                {coupons?.length} coupon{coupons?.length !== 1 ? "s" : ""} configured
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Discount Rules</TableHead>
                    <TableHead>Applies To</TableHead>
                    <TableHead>Uses</TableHead>
                    <TableHead>Valid Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coupons?.map((coupon) => (
                    <TableRow key={coupon.id} data-testid={`coupon-row-${coupon.code}`}>
                      <TableCell>
                        <code className="font-mono bg-muted px-2 py-1 rounded">
                          {coupon.code}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm max-w-xs truncate" title={formatRulesSummary(coupon.rules)}>
                          {coupon.rules && coupon.rules.length > 0 ? (
                            <span className="flex items-center gap-1 flex-wrap">
                              {coupon.rules.map((rule, index) => (
                                <span key={index} className="flex items-center gap-1">
                                  <Badge variant="secondary" className="gap-1">
                                    {rule.discountType === "percentage" ? (
                                      <Percent className="h-3 w-3" />
                                    ) : (
                                      <DollarSign className="h-3 w-3" />
                                    )}
                                    {formatDiscount(rule.discountType, rule.discountValue)}
                                    {rule.durationMonths && (
                                      <span className="text-xs opacity-75">
                                        /{rule.durationMonths}mo
                                      </span>
                                    )}
                                  </Badge>
                                  {index < coupon.rules.length - 1 && (
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                  )}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <Badge variant="secondary" className="gap-1">
                              {coupon.discountType === "percentage" ? (
                                <Percent className="h-3 w-3" />
                              ) : (
                                <DollarSign className="h-3 w-3" />
                              )}
                              {formatDiscount(coupon.discountType, coupon.discountValue)}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[150px] truncate" title={getTierDisplayNames(coupon.appliesToTiers)}>
                          {coupon.appliesToTiers && coupon.appliesToTiers.length > 0 ? (
                            <span className="text-sm">{getTierDisplayNames(coupon.appliesToTiers)}</span>
                          ) : (
                            <span className="text-muted-foreground">All tiers</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {coupon.usedCount}
                        {coupon.maxUses ? ` / ${coupon.maxUses}` : ""}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {coupon.validFrom || coupon.validUntil ? (
                            <span>
                              {formatDate(coupon.validFrom)} - {formatDate(coupon.validUntil)}
                            </span>
                          ) : (
                            <span>No expiry</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={coupon.isActive}
                          onCheckedChange={() => toggleActive(coupon)}
                          data-testid={`switch-coupon-active-${coupon.code}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            size="icon" 
                            variant="ghost"
                            onClick={() => openEditDialog(coupon)}
                            data-testid={`button-edit-coupon-${coupon.code}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost"
                            onClick={() => openDeleteDialog(coupon)}
                            data-testid={`button-delete-coupon-${coupon.code}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {selectedCoupon ? "Edit Coupon" : "Create Coupon"}
            </DialogTitle>
            <DialogDescription>
              {selectedCoupon 
                ? "Update the coupon code settings and discount rules"
                : "Create a new discount coupon with flexible pricing rules"
              }
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Coupon Code</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    placeholder="SUMMER2024"
                    className="font-mono"
                    data-testid="input-coupon-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxUses">Max Uses (Optional)</Label>
                  <Input
                    id="maxUses"
                    type="number"
                    value={formData.maxUses}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxUses: e.target.value }))}
                    placeholder="Unlimited"
                    data-testid="input-max-uses"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bonusLeadCredits">Sign-on Bonus Lead Credits (Optional)</Label>
                <Input
                  id="bonusLeadCredits"
                  type="number"
                  min="0"
                  value={formData.bonusLeadCredits}
                  onChange={(e) => setFormData(prev => ({ ...prev, bonusLeadCredits: e.target.value }))}
                  placeholder="0"
                  data-testid="input-coupon-bonus-lead-credits"
                />
                <p className="text-xs text-muted-foreground">
                  Bonus Lead Gen credits awarded once when this coupon is redeemed at signup. Lands in the rollover bucket.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Summer promotion discount"
                  data-testid="input-coupon-description"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Discount Rules</Label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={addRule}
                    data-testid="button-add-rule"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Phase
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Define multi-phase discounts. Rules apply in order (e.g., "100% off for 3 months, then 50% off forever").
                </p>
                <div className="space-y-3">
                  {formData.rules.map((rule, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-3 p-3 border rounded-md bg-muted/30"
                      data-testid={`rule-${index}`}
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1 grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Duration</Label>
                          <Input
                            type="number"
                            value={rule.durationMonths ?? ""}
                            onChange={(e) => updateRule(index, { 
                              durationMonths: e.target.value ? parseInt(e.target.value) : null 
                            })}
                            placeholder="Forever"
                            className="h-9"
                            data-testid={`input-rule-duration-${index}`}
                          />
                          <p className="text-xs text-muted-foreground">months (empty = forever)</p>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Type</Label>
                          <Select
                            value={rule.discountType}
                            onValueChange={(value: "percentage" | "fixed") => 
                              updateRule(index, { discountType: value })
                            }
                          >
                            <SelectTrigger className="h-9" data-testid={`select-rule-type-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percentage">Percentage</SelectItem>
                              <SelectItem value="fixed">Fixed ($)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            {rule.discountType === "percentage" ? "Percent Off" : "Amount Off ($)"}
                          </Label>
                          <Input
                            type="number"
                            value={rule.discountValue}
                            onChange={(e) => updateRule(index, { 
                              discountValue: parseInt(e.target.value) || 0 
                            })}
                            placeholder="0"
                            className="h-9"
                            data-testid={`input-rule-value-${index}`}
                          />
                        </div>
                      </div>
                      {formData.rules.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRule(index)}
                          data-testid={`button-remove-rule-${index}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Applies To Tiers</Label>
                <p className="text-sm text-muted-foreground">
                  Select which subscription tiers this coupon can be used with. Leave all unchecked for all tiers.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {tiers?.map((tier) => (
                    <div 
                      key={tier.id} 
                      className="flex items-center space-x-2 p-2 border rounded-md hover-elevate cursor-pointer"
                      onClick={() => toggleTier(tier.id)}
                    >
                      <Checkbox
                        id={`tier-${tier.id}`}
                        checked={formData.selectedTiers.includes(tier.id)}
                        onCheckedChange={() => toggleTier(tier.id)}
                        data-testid={`checkbox-tier-${tier.name}`}
                      />
                      <Label 
                        htmlFor={`tier-${tier.id}`} 
                        className="flex-1 cursor-pointer"
                      >
                        {tier.displayName || tier.name}
                      </Label>
                    </div>
                  ))}
                </div>
                {formData.selectedTiers.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">
                    No tiers selected - coupon will apply to all tiers
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="validFrom">Valid From</Label>
                  <Input
                    id="validFrom"
                    type="date"
                    value={formData.validFrom}
                    onChange={(e) => setFormData(prev => ({ ...prev, validFrom: e.target.value }))}
                    data-testid="input-valid-from"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="validUntil">Valid Until</Label>
                  <Input
                    id="validUntil"
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
                    data-testid="input-valid-until"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-md">
                <div>
                  <Label htmlFor="isActive">Active</Label>
                  <p className="text-sm text-muted-foreground">
                    Only active coupons can be used at checkout
                  </p>
                </div>
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
                  data-testid="switch-coupon-is-active"
                />
              </div>
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
              data-testid="button-save-coupon"
            >
              {createMutation.isPending || updateMutation.isPending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : selectedCoupon ? (
                "Update Coupon"
              ) : (
                "Create Coupon"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Coupon</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the coupon "{selectedCoupon?.code}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedCoupon && deleteMutation.mutate(selectedCoupon.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-coupon"
            >
              {deleteMutation.isPending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function PlatformCoupons() {
  return <PlatformLayout><CouponsContent /></PlatformLayout>;
}
